import type { CheckboxProps, StackProps } from "@mui/material";
import { type Context, createContext, useContext, useMemo } from "react";
import { useGroupedVirtualizedSelect } from "./logics";

export interface IGroupedVirtualizedSelectContext<Data> extends ReturnType<typeof useGroupedVirtualizedSelect<Data>> {
  hideCheckboxes: boolean;
  slotProps?: {
    selectHeader?: Partial<StackProps>;
    selectHeaderCheckbox?: Partial<CheckboxProps>;
    selectGroup?: Partial<StackProps>;
    selectGroupCheckbox?: Partial<CheckboxProps>;
    selectItem?: Partial<StackProps>;
    selectItemCheckbox?: Partial<CheckboxProps>;
  };
}

export interface IGroupedVirtualizedSelectProviderProps<Data> {
  data: readonly Data[];
  groupCounts: readonly number[];
  selectedItems?: readonly Data[];
  onSelectedItemsChange?: (items: readonly Data[]) => void;
  getItemKey: (item: Data) => string;
  hideCheckboxes: boolean;
  slotProps?: IGroupedVirtualizedSelectContext<Data>["slotProps"];
  children: React.ReactNode;
}

const context = createContext<IGroupedVirtualizedSelectContext<unknown> | undefined>(undefined);

export const useGroupedVirtualizedSelectContext = <Data,>() => {
  const value = useContext(context);
  if (value === undefined) {
    throw new Error("Context must be called inside his provider");
  }
  return value as IGroupedVirtualizedSelectContext<Data>;
};

export const GroupedVirtualizedSelectProvider = <Data,>({
  data,
  groupCounts,
  selectedItems,
  onSelectedItemsChange,
  getItemKey,
  hideCheckboxes,
  slotProps,
  children,
}: IGroupedVirtualizedSelectProviderProps<Data>) => {
  const states = useGroupedVirtualizedSelect(data, groupCounts, selectedItems, onSelectedItemsChange, getItemKey);
  const Provider = context.Provider as Context<IGroupedVirtualizedSelectContext<Data> | undefined>["Provider"];

  const value = useMemo(
    () => ({
      slotProps,
      hideCheckboxes,
      ...states,
    }),
    [slotProps, hideCheckboxes, states]
  );

  return <Provider value={value}>{children}</Provider>;
};
